import React, { useState, useEffect, useRef } from 'react';
import { Support } from '../../hooks/Support';

export type ChromeCastProps = {
  authorization: string;
  enabled: boolean;
  code: string;
  video: string;
  videoName: string;
  videoPoster: string;
  supported: Support;
  subtitles: {
    label: string;
    src: string;
    language: string;
  }[];
};

declare global {
  interface Window {
    isGCastApiAvailable: boolean;
    cast: any;
    chrome: any;
    __onGCastApiAvailable?: (isAvailable: boolean) => void;
  }
}

window['__onGCastApiAvailable'] = function(isAvailable) {
  if (isAvailable) {
    setTimeout(() => {
      window.isGCastApiAvailable = true;
      window.dispatchEvent(new Event('cast-api-available'));
    }, 1000);
  } else {
    window.isGCastApiAvailable = false;
  }
};

const ChromeCast = ({ authorization, enabled, code, video, videoName, videoPoster, supported, subtitles }: ChromeCastProps) => {
  const [isChromeCast, setIsChromecast] = React.useState<boolean>(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [castInitialized, setCastInitialized] = useState(false);
  const [castSession, setCastSession] = useState<any>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const castContext = useRef<any>(null);
  const retryTimeoutRef = useRef<any>(null);
  const maxRetries = 3;
  const retryCount = useRef(0);
  const poster = videoPoster || '';
  const src = video || '';

  /*
  const chromecastVideo = () => {
    if (!supported.chromecast) {
      return;
    }

    // const source = document.createElement('source');
    // source.src = video;
    // source.type = 'application/x-mpegURL';

    const rootElement = document.querySelector(`[data-code="${code}"]`) as HTMLDivElement;
    const videoElement = rootElement.querySelector('video') as HTMLVideoElement;
    // console.log('video', videoElement);
    // videoElement.appendChild(source);
    videoElement.disableRemotePlayback = false;

    const chrome = (window as any).chrome;
    const cast = (chrome as any).cast;

    let session = null;

    function sessionListener(event: any) {
      session = event;
      console.log('New session');

      if (session.media.length != 0) {
        console.log('Found ' + session.media.length + ' sessions.');
      }
    }

    function receiverListener(event: any) {
      if(event === 'available' ) {
        console.log("Chromecast was found on the network.", event);
      } else {
        console.log("There are no Chromecasts available.", event);
      }
    }

    // const context = window.cast.framework.CastReceiverContext.getInstance();
    // context.setLoggerLevel(window.cast.framework.LoggerLevel.DEBUG);

    // // Para capturar erros específicos de mídia
    // const playerManager = context.getPlayerManager();
    // playerManager.addEventListener(
    //   window.cast.framework.events.EventType.ERROR, (event: any) => {
    //     console.log('Media Error:', event);
    // });

    // const context = chrome.cast.framework.CastReceiverContext.getInstance();

    // context.start({
    //   // The default HLS player is inferior to Shaka Player so we default to using shaka instead.
    //   // You can read more here: https://developers.google.com/cast/docs/web_receiver/shaka_migration
    //   useShakaForHls: true,
    // });

    const sessionRequest = new cast.SessionRequest(chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID);

    const apiConfig = new cast.ApiConfig(sessionRequest, sessionListener, receiverListener);

    const link = `${video}&authorization=${authorization}`;

    (window as any).cast.framework.setLoggerLevel((window as any).cast.framework.LoggerLevel.DEBUG);

    cast.initialize(apiConfig, () => {
      cast.requestSession((session: any) => {
        const mediaInfo = new cast.media.MediaInfo(link, 'application/vnd.apple.mpegurl');

        mediaInfo.streamType = cast.media.StreamType.BUFFERED;
        mediaInfo.contentType = 'application/vnd.apple.mpegurl';
        mediaInfo.hlsVideoSegmentFormat = window.chrome.cast.media.HlsVideoSegmentFormat.FMP4;
        mediaInfo.hlsSegmentFormat = window.chrome.cast.media.HlsSegmentFormat.FMP4;

        mediaInfo.metadata = new cast.media.GenericMediaMetadata();
        mediaInfo.metadata.type = chrome.cast.media.MetadataType.GENERIC;
        mediaInfo.metadata.title = videoName;
        mediaInfo.startAbsoluteTime = 0;
        mediaInfo.metadata.images = [
          new cast.Image(videoPoster || ''),
        ];

        mediaInfo.tracks = subtitles.map((subtitle, index) => {
          const subtitleTrack = new window.chrome.cast.media.Track(index + 1, cast.media.TrackType.TEXT);

          subtitleTrack.trackContentId = subtitle.src;
          subtitleTrack.trackContentType = 'text/vtt';
          subtitleTrack.name = subtitle.label;
          subtitleTrack.subtype = window.chrome.cast.media.TextTrackType.SUBTITLES;

          return subtitleTrack;
        });

        mediaInfo.textTrackStyle = new window.chrome.cast.media.TextTrackStyle();
        mediaInfo.textTrackStyle.foregroundColor = '#FFFFFF';
        mediaInfo.textTrackStyle.backgroundColor = '#000000';
        mediaInfo.textTrackStyle.fontScale = 1.0;

        mediaInfo.customData = {
          startTime: 0.01,
          autoplay: true,
          preloadTime: 15,
          initialBufferTime: 0.01,
          bufferMode: 'adaptive',
          startAbsoluteTime: 0.01,
          initSegmentRef: true,
          forceInitialPlayback: true,
          enableInbandCaptions: true,
          forceCaptions: true,
        };

        console.log('Media info', mediaInfo);

        const request = new cast.media.LoadRequest(mediaInfo);
        request.autoplay = true;
        request.currentTime = 0.01;
        request.activeTrackIds = [1];

        session.loadMedia(request, (...args: any) => {
          console.log('Media loaded', args);
        }, (...args: any) => {
          console.log('Media error', args);
        });

        setIsChromecast(true);
      });
    }, (error: any) => {
      setIsChromecast(false);
    });
  }
  */

  const initializeCastApi = () => {
    const chrome = window?.chrome as any;
    const cast = window?.cast as any;

    try {
      if (!window.cast || !window.cast.framework) {
        console.log('Cast API not available yet, retrying...');
        if (retryCount.current < maxRetries) {
          retryTimeoutRef.current = setTimeout(() => {
            retryCount.current += 1;
            initializeCastApi();
          }, 1000);
        }
        return;
      }

      const context = window.cast.framework.CastContext.getInstance();
      context.setOptions({
        receiverApplicationId: chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID,
        autoJoinPolicy: chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
        language: 'en-US',
        resumeSavedSession: true
      });

      castContext.current = context;

      // Listen for cast state changes
      context.addEventListener(
        cast.framework.CastContextEventType.SESSION_STATE_CHANGED,
        (event: any) => {
          console.log('Cast session state changed:', event.sessionState);
          switch (event.sessionState) {
            case cast.framework.SessionState.SESSION_STARTED:
            case cast.framework.SessionState.SESSION_RESUMED:
              const currentSession = context.getCurrentSession();
              if (currentSession) {
                console.log('Session established successfully');
                setCastSession(currentSession);
                setIsConnecting(false);
                // Auto load media when session starts
                if (!currentSession.getMediaSession()) {
                  setTimeout(() => loadMedia(currentSession), 1000);
                }
              }
              break;
            case cast.framework.SessionState.SESSION_ENDED:
              console.log('Session ended');
              setCastSession(null);
              setIsConnecting(false);
              break;
            case cast.framework.SessionState.SESSION_STARTING:
              console.log('Session starting');
              setIsConnecting(true);
              break;
            case cast.framework.SessionState.NO_SESSION:
              console.log('No session');
              setCastSession(null);
              setIsConnecting(false);
              break;
          }
        }
      );

      // Check for existing session
      const currentSession = context.getCurrentSession();
      if (currentSession) {
        console.log('Found existing session');
        setCastSession(currentSession);
      }

      setCastInitialized(true);
      console.log('Cast API initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Cast API:', error);
      if (retryCount.current < maxRetries) {
        console.log(`Retrying initialization (${retryCount.current + 1}/${maxRetries})...`);
        retryTimeoutRef.current = setTimeout(() => {
          retryCount.current += 1;
          initializeCastApi();
        }, 1000);
      } else {
        setCastInitialized(false);
        setIsConnecting(false);
      }
    }
  };

  const endCastSession = async () => {
    try {
      if (castContext.current && castSession) {
        const currentSession = castContext.current.getCurrentSession();
        if (currentSession) {
          console.log('Ending cast session...');
          await currentSession.endSession();
          setCastSession(null);
        }
      }
    } catch (error) {
      console.error('Error ending cast session:', error);
    }
  };

  useEffect(() => {
    // Try to initialize immediately
    initializeCastApi();

    // Also listen for the event
    window.addEventListener('cast-api-available', initializeCastApi);

    // Add beforeunload event listener
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      endCastSession();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('cast-api-available', initializeCastApi);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      // Clean up cast session when component unmounts
      endCastSession();
    };
  }, []);

  const loadMedia = async (session: any) => {
    const chrome = window?.chrome as any;
    const cast = window?.cast as any;

    if (!session) {
      console.error('No session available for loading media');
      setIsConnecting(false);
      return;
    }

    try {
      // Create media info for FMP4/HLS content
      const mediaInfo = new chrome.cast.media.MediaInfo(src, 'application/x-mpegURL');
      mediaInfo.streamType = chrome.cast.media.StreamType.BUFFERED;
      mediaInfo.hlsSegmentFormat = chrome.cast.media.HlsSegmentFormat.FMP4;
      mediaInfo.hlsVideoSegmentFormat = chrome.cast.media.HlsVideoSegmentFormat.FMP4;

      // Add metadata
      if (poster) {
        mediaInfo.metadata = new chrome.cast.media.GenericMediaMetadata();
        mediaInfo.metadata.images = [{ url: poster }];
      }

      // Configure media loading options
      const loadRequest = new chrome.cast.media.LoadRequest(mediaInfo);
      loadRequest.autoplay = true;
      loadRequest.currentTime = videoRef.current?.currentTime || 0;

      console.log('Loading media to cast session...');
      await session.loadMedia(loadRequest);
      console.log('Media loaded successfully');
      setIsConnecting(false);
    } catch (error) {
      console.error('Error loading media:', error);
      setIsConnecting(false);
      // If media loading fails, try to reset the session
      setCastSession(null);
    }
  };

  const handleCastClick = async () => {
    if (!castInitialized || !castContext.current) {
      console.log('Cast not initialized, retrying initialization...');
      retryCount.current = 0;
      initializeCastApi();
      return;
    }

    if (isConnecting) return;

    try {
      setIsConnecting(true);
      console.log('Starting cast session...');

      if (!castSession) {
        const context = castContext.current;
        // Force a new session
        await context.requestSession();
      } else {
        console.log('Using existing session');
        await loadMedia(castSession);
      }
    } catch (error) {
      console.error('Error handling cast:', error);
      setIsConnecting(false);
      // Reset session state and try again
      setCastSession(null);
    }
  };


  if (!enabled || !supported.chromecast) {
    return null;
  }

  return (
    <div className={`${isChromeCast ? 'smartplayer__chromecast--active' : 'smartplayer__chromecast--inactive'}`} onClick={() => handleCastClick()}>
      <button className="smartplayer__bar-button" type="button">
        <div className="icon icon--chromecast-active">
          <svg xmlns="http://www.w3.org/2000/svg" height="32" width="32" viewBox="0 0 512 512" fill="currentColor">
            <path d="M447.8 64H64c-23.6 0-42.7 19.1-42.7 42.7v63.9H64v-63.9h383.8v298.6H298.6V448H448c23.6 0 42.7-19.1 42.7-42.7V106.7C490.7 83.1 471.4 64 447.8 64zM21.3 383.6L21.3 383.6l0 63.9h63.9C85.2 412.2 56.6 383.6 21.3 383.6L21.3 383.6zM21.3 298.6V341c58.9 0 106.6 48.1 106.6 107h42.7C170.7 365.6 103.7 298.7 21.3 298.6zM213.4 448h42.7c-.5-129.5-105.3-234.3-234.8-234.6l0 42.4C127.3 255.6 213.3 342 213.4 448z"/>
          </svg>
        </div>
        <div className="icon icon--chromecast-inactive">
          <svg xmlns="http://www.w3.org/2000/svg" height="32" width="32" viewBox="0 0 512 512" fill="currentColor">
            <path d="M447.8 64H64c-23.6 0-42.7 19.1-42.7 42.7v63.9H64v-63.9h383.8v298.6H298.6V448H448c23.6 0 42.7-19.1 42.7-42.7V106.7C490.7 83.1 471.4 64 447.8 64zM21.3 383.6L21.3 383.6l0 63.9h63.9C85.2 412.2 56.6 383.6 21.3 383.6L21.3 383.6zM21.3 298.6V341c58.9 0 106.6 48.1 106.6 107h42.7C170.7 365.6 103.7 298.7 21.3 298.6zM213.4 448h42.7c-.5-129.5-105.3-234.3-234.8-234.6l0 42.4C127.3 255.6 213.3 342 213.4 448z"/>
          </svg>
        </div>
      </button>
    </div>
  );
};

export default ChromeCast;
